import { Component } from "@angular/core";
import { MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";

export interface CaptchaTile {
  readonly col: number;
  readonly row: number;
  /** Whether this square contains the handsome stud (left column of the 3×4 split). */
  readonly isStud: boolean;
  selected: boolean;
}

/** 3 columns × 4 rows = 12 squares; image is sliced via CSS background (single asset). */
const COLS = 3;
const ROWS = 4;

/**
 * Which tiles contain “the stud” (same order as the grid: row 0 left→right, then row 1, …).
 * Index map:
 *   0  1  2
 *   3  4  5
 *   6  7  8
 *   9 10 11
 */
const STUD_TILES: readonly boolean[] = [
  false,
  false,
  false, // row 0
  true,
  false,
  false, // row 1
  true,
  false,
  false, // row 2
  true,
  false,
  false // row 3 — left column = stud (edit this array to change correct squares)
];

@Component({
  selector: "app-captcha-dialog",
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatIconModule],
  templateUrl: "./captcha-dialog.component.html",
  styleUrl: "./captcha-dialog.component.scss"
})
export class CaptchaDialogComponent {
  /** Shown after a failed verify attempt. */
  errorHint = false;

  /** One shared photo; each tile shows a slice via `spritePosition`. */
  readonly imageSrc = "assets/captcha-selfie.png";

  readonly cols = COLS;
  readonly rows = ROWS;

  readonly tiles: CaptchaTile[] = Array.from({ length: COLS * ROWS }, (_, i) => {
    const col = i % COLS;
    const row = Math.floor(i / COLS);
    return {
      col,
      row,
      isStud: STUD_TILES[i] === true,
      selected: false
    };
  });

  constructor(readonly ref: MatDialogRef<CaptchaDialogComponent, boolean>) {}

  /** Positions the background so this tile shows the correct 1/12 of the image. */
  spritePosition(col: number, row: number): string {
    const xPct = COLS <= 1 ? 0 : (col * 100) / (COLS - 1);
    const yPct = ROWS <= 1 ? 0 : (row * 100) / (ROWS - 1);
    return `${xPct}% ${yPct}%`;
  }

  toggle(i: number): void {
    const t = this.tiles[i];
    if (!t) return;
    t.selected = !t.selected;
    this.errorHint = false;
  }

  verify(): void {
    const ok = this.tiles.every((tile) => tile.selected === tile.isStud);
    if (ok) {
      this.ref.close(true);
      return;
    }
    this.errorHint = true;
  }

  skipHumanity(): void {
    this.ref.close(false);
  }
}
